/**
 * Shared aura strip component (#1152).
 *
 * Renders a flex container of `<AuraChip />` elements from a list of `WireAura`s.
 * Used across HUD surfaces (SelfAuraBar rows, MajorAuraRow, etc.) to eliminate
 * duplicate flex row wrapper and chip iteration boilerplate.
 *
 * PURELY COSMETIC / CLIENT-SIDE.
 */

import type { CSSProperties, MouseEvent, ReactElement } from "react";
import type { AuraDefId, WireAura } from "@pit/protocol";
import { AuraChip, DEFAULT_AURA_CHIP_STYLE, type AuraChipStyle } from "./AuraChip.js";

/** Sim tick rate default — matches MatchScreen / SelfAuraBar / Countdown. */
const DEFAULT_TICK_RATE = 30;

export interface AuraStripProps {
  /** The list of auras to render. */
  readonly auras: readonly WireAura[];
  /** Current sim tick (for each chip's countdown). */
  readonly now: number;
  /** Ticks per second (defaults to 30). */
  readonly tickRate?: number | undefined;
  /** Visual style for the individual aura chips. */
  readonly chipStyle?: AuraChipStyle | undefined;
  /** Optional container flex / layout styles. */
  readonly style?: CSSProperties | undefined;
  /** Overridable test id for the container element (e.g. "major-aura-row", "self-buff-row"). */
  readonly testId?: string | undefined;
  /** Overridable test id for each child chip (e.g. "major-aura-chip", "self-aura-chip"). */
  readonly chipTestId?: string | undefined;
  /** Optional callback when right-clicking an aura chip (e.g. cancel buff). */
  readonly onCancelAura?: ((def: AuraDefId) => void) | undefined;
}

export function AuraStrip({
  auras,
  now,
  tickRate = DEFAULT_TICK_RATE,
  chipStyle = DEFAULT_AURA_CHIP_STYLE,
  style,
  testId,
  chipTestId,
  onCancelAura,
}: AuraStripProps): ReactElement {
  const container: CSSProperties = {
    display: "flex",
    ...style,
  };

  return (
    <div
      {...(testId !== undefined ? { "data-testid": testId } : {})}
      data-count={String(auras.length)}
      style={container}
    >
      {auras.map((aura) => (
        <AuraChip
          key={`${aura.def}:${String(aura.sourceSeat)}:${String(aura.appliedAtTick)}`}
          aura={aura}
          now={now}
          tickRate={tickRate}
          style={chipStyle}
          {...(chipTestId !== undefined ? { testId: chipTestId } : {})}
          {...(onCancelAura !== undefined
            ? {
                onContextMenu: (e: MouseEvent<HTMLDivElement>) => {
                  // #752 CARVE-OUT: suppress browser context menu and stop
                  // propagation so RMB does not trigger movement or bubble up.
                  e.preventDefault();
                  e.stopPropagation();
                  onCancelAura(aura.def);
                },
              }
            : {})}
        />
      ))}
    </div>
  );
}
